import {Component, NgZone} from "@angular/core";
import "rxjs/add/operator/map";
import "rxjs/add/operator/subscribeOn";
import {ModalConfig} from "../services/notification/modal.config";
import {EventBus} from "../event/EventBus";
import {ActivityEventData} from "../event/ActivityEvent";
import {BugsnagLoggingService} from "../services/logging/bugsnag-logging.service";
import {DebugEvent, DebugCommand} from "../event/DebugEvent";
import {ComponentBase} from "../base/ComponentBase";
import {RaVersion} from "../services/configuration/ra.version";
import {ContentCursor} from "../services/content/content.cursor";
import {StepService} from "../services/step/step.service";
import {CssSentence, ContentCssClass} from "../content/content-view.objects";
import {SessionService} from "../services/configuration/session.service";
import {UserModelService} from "../services/usermodel/usermodel.service";
import {RecordingObjects, BookSessionData} from "../services/recording/recording.objects";
import {AssignmentService} from "../services/assignment/assignment.service";
import {DebugData} from "./debug-data.objects";
import {AudioQueueService} from "../services/audio-playback/audioqueue.service";
import {filter} from "rxjs/operators";
import {BookSessionType} from "../constants";

@Component({
               selector: "debug-modal",
               templateUrl: "./debug.modal.component.html",
               styleUrls: ["../message/message.modal.component.css"]
           })
export class DebugModalComponent extends ComponentBase {

    private modalType: string = "debugrecording";
    public activateModal: boolean = false;
    public status:string = "";
    public simulatedWCPM = 0;
    private audioLengthPerWord = 0;
    private recordingObjects: RecordingObjects;
    private debugData: DebugData;

    constructor(
        private eventBus: EventBus,
        private modalConfig: ModalConfig,
        private stepService: StepService,
        public contentCursor: ContentCursor,
        private logService: BugsnagLoggingService,
        private userModelService: UserModelService,
        private sessionService: SessionService,
        private assignmentService: AssignmentService,
        private audioQueueService: AudioQueueService) {
        super("DebugModalComponent");
        this.recordingObjects = new RecordingObjects();
        this.debugData = new DebugData();
        this.subscribeObserve();
    }

    private subscribeObserve():void {
        let sub = this.eventBus.pipe(filter((e: DebugEvent) =>
                                           (e.type === DebugEvent.TYPE &&
                                            e.command === DebugCommand.DEBUG_DIALOG))).subscribe((e: DebugEvent) => {
            if (this.stepService.isFluency && this.userModelService.currentSentenceId <= 0) {
                this.activateModal = true;
            } else {
                this.logService.log("Can only launch debug record dialog in Step 2 with no previous partial recording");
            }
        });
        this.subscriptions.push(sub);
    }

    public close(): void {
        this.activateModal = false;
        this.modalConfig.removeModal(new ActivityEventData({type: this.modalType}));
    }

    public startDebugRecording(): void {
        this.audioQueueService.reset().subscribe(() => {
            this.sessionService.startBookSession(this.assignmentService.currentSelection, BookSessionType.FLUENCY).subscribe(() => {
                this.logService.log("starting debug recording with wcpm: " + this.simulatedWCPM);
                this.audioLengthPerWord = this.calculateWordLength();
                this.simulatedWCPM = 0; // reset so we have to enter a new number in the UI
                this.close();
                this.contentCursor.setMode(ContentCssClass.MODE_RECORDING);
                this.contentCursor.selectSentence(this.contentCursor.firstSentence);
                this.debugRecordSentence();
            });
        });
    }

    private calculateWordLength(): number {
        return (60000 / this.simulatedWCPM);
    }

    public wcpmValid(): boolean {
        return this.simulatedWCPM > 0;
    }

    private debugRecordSentence():void {
        let sentence:CssSentence = this.contentCursor.selectedSentence;
        this.contentCursor.goNextSentence();
        let isEndOfSelection: boolean = !this.contentCursor.selectedSentence;
        // TODO: create dummy upload
        let data = this.debugData.getAudio(sentence.id);
        this.recordingObjects.addDebugWordRecordData(sentence, this.audioLengthPerWord);
        let uploadData: BookSessionData = this.recordingObjects.createUploadData(sentence, data, isEndOfSelection);
        this.logService.log("isEndOfSelection:" + isEndOfSelection
                            + " recordingState:" + JSON.stringify(uploadData.learnerStory));

        this.sessionService.sendBookSessionData(uploadData,
                                                this.onRecordUploaded, this.onRecordUploadError).subscribe((s) => {
            this.logService.log("upload event: " + s);
        });
    }

    onRecordUploaded = (data) => {
        this.logService.log("uploaded record " + JSON.stringify(data));
        if (this.contentCursor.selectedSentence) {
            this.debugRecordSentence();
        }
    };

    onRecordUploadError = () => {
        this.modalConfig.openMessageModal("upload_error");
        this.logService.log("Failed to upload recording data");
    };
}
